import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DashboardLayout from '../../components/DashboardLayout';
import ModularGridEditor from '../../components/admin/ModularGridEditor';
import CarouselBannerEditor from '../../components/admin/CarouselBannerEditor';
import PromotionCardsGridEditor from '../../components/admin/PromotionCardsGridEditor';
import PartnerCardsGridEditor from '../../components/admin/PartnerCardsGridEditor';
import NavigationCardsEditor from '../../components/admin/NavigationCardsEditor';
import RichTextEditor from '../../components/admin/RichTextEditor';
import ExternalPaddingControl from '../../components/admin/ExternalPaddingControl';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { GripVertical, Plus, Edit, Trash2, Eye, EyeOff, ArrowLeft, Image, Type, Package, Gift, Navigation } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { DestructiveActionDialog } from '../../lib/DestructiveActionDialog';
import { getAdminBlockTypes } from '../../lib/cmsBlockRegistry';

const AdminPageBlocks = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [visualMode, setVisualMode] = useState('url'); // 'url' ou 'upload'
  
  // Nouveaux états pour blocs globaux
  const [blockCategory, setBlockCategory] = useState('classic'); // 'classic' | 'global'
  const [globalBlocks, setGlobalBlocks] = useState([]);
  const [selectedGlobalBlockId, setSelectedGlobalBlockId] = useState('');
  
  const [formData, setFormData] = useState({
    type: 'banner_fixed_centered',
    title: '',
    label: '',
    content: {},
    is_active: true,
    zone: 'content', // header, menu, content, footer
    order: 0 // Auto-calculé côté backend
  });

  // Détecter si la page est un listing hybride (page-partners, etc.)
  // Les pages listing ont un ID dans LISTING_PAGE_IDS
  const LISTING_PAGE_IDS = ['page-partners'];
  const isListingPage = LISTING_PAGE_IDS.includes(pageId);

  useEffect(() => {
    if (pageId) {
      loadPageAndBlocks();
      loadGlobalBlocks();
    }
  }, [pageId]);

  const loadPageAndBlocks = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/pages/${pageId}`);
      setPage(response.data);
      setBlocks(response.data.blocks || []);
    } catch (error) {
      console.error('Error loading page:', error);
      toast.error('Erreur lors du chargement de la page');
    } finally {
      setLoading(false);
    }
  };

  const loadGlobalBlocks = async () => {
    try {
      const response = await api.get('/global-blocks');
      setGlobalBlocks(response.data || []);
    } catch (error) {
      console.error('Error loading global blocks:', error);
    }
  };

  const handleOpenModal = (block = null) => {
    if (block) {
      setEditingBlock(block);
      
      // Détecter si c'est un bloc global référencé
      if (block.type === 'global_block_reference' && block.content?.global_block_id) {
        setBlockCategory('global');
        setSelectedGlobalBlockId(block.content.global_block_id);
        setFormData({
          type: 'global_block_reference',
          title: '',
          label: block.label || block.title || '',
          content: block.content,
          is_active: block.is_active,
          zone: block.zone || 'content',
          order: block.order
        });
      } else {
        setBlockCategory('classic');
        setFormData({
          type: block.type,
          title: '',
          label: block.label || block.title || '',
          content: block.content || {},
          is_active: block.is_active,
          zone: block.zone || 'content',
          order: block.order
        });
        
        if (block.content?.uploaded_image) {
          setVisualMode('upload');
        } else {
          setVisualMode('url');
        }
      }
    } else {
      setEditingBlock(null);
      setBlockCategory('classic');
      setSelectedGlobalBlockId('');
      setFormData({
        type: 'banner_fixed_centered',
        title: '',
        label: '',
        content: {},
        is_active: true,
        zone: isListingPage ? 'header' : 'content',
        order: blocks.length
      });
      setVisualMode('url');
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBlock(null);
    setBlockCategory('classic');
    setSelectedGlobalBlockId('');
    setFormData({
      type: 'banner_fixed_centered',
      title: '',
      label: '',
      content: {},
      is_active: true,
      zone: 'content',
      order: 0
    });
    setVisualMode('url');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.label) {
      toast.error('Le libellé est obligatoire');
      return;
    }
    
    // Validation pour blocs globaux
    if (blockCategory === 'global') {
      if (!selectedGlobalBlockId) {
        toast.error('Veuillez sélectionner un bloc global');
        return;
      }
      
      // Trouver le bloc global sélectionné
      const selectedGlobalBlock = globalBlocks.find(gb => gb.id === selectedGlobalBlockId);
      if (!selectedGlobalBlock) {
        toast.error('Bloc global introuvable');
        return;
      }
      
      // Créer une référence au bloc global
      const blockData = {
        type: 'global_block_reference',
        title: formData.label || 'Bloc global',
        label: formData.label,
        content: {
          global_block_id: selectedGlobalBlock.id,
          global_block_type: selectedGlobalBlock.type,
          global_block_title: selectedGlobalBlock.title
        },
        is_active: formData.is_active,
        zone: formData.zone, // Auto-calcul de l'order
        order: formData.order,
        page_id: pageId
      };
      
      try {
        if (editingBlock) {
          await api.put(`/blocks/${editingBlock.id}`, blockData);
          toast.success('Bloc global modifié avec succès');
        } else {
          await api.post('/blocks', blockData);
          toast.success('Bloc global ajouté avec succès');
        }
        
        loadPageAndBlocks();
        handleCloseModal();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
      }
      return;
    }
    
    // Validation pour blocs classiques (optionnelle, pas bloquante)
    if (formData.type === 'banner_fixed_centered' || formData.type === 'banner') {
      if (!formData.content.title && !formData.content.text) {
        console.warn('⚠️ Bannière sans titre ni texte');
      }
    }
    
    try {
      const blockData = {
        ...formData,
        title: formData.label || 'Bloc sans titre',
        page_id: pageId
      };

      if (editingBlock) {
        await api.put(`/blocks/${editingBlock.id}`, blockData);
        toast.success('Bloc modifié avec succès');
      } else {
        await api.post('/blocks', blockData);
        toast.success('Bloc créé avec succès');
      }
      
      loadPageAndBlocks();
      handleCloseModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/blocks/${deleteTarget.id}`);
      toast.success('Bloc supprimé');
      setDeleteTarget(null);
      loadPageAndBlocks();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleBlockStatus = async (blockId, currentStatus) => {
    try {
      await api.put(`/blocks/${blockId}`, { is_active: !currentStatus });
      toast.success('Statut mis à jour');
      loadPageAndBlocks();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(blocks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setBlocks(items);

    try {
      const blockIds = items.map(item => item.id);
      await api.post('/blocks/reorder', {
        page_id: pageId,
        block_ids: blockIds
      });
      toast.success('Ordre mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la réorganisation');
      loadPageAndBlocks();
    }
  };

  const updateContentField = (field, value) => {
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        [field]: value
      }
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({
        ...formData,
        content: {
          ...formData.content,
          uploaded_image: reader.result,
          image: ''
        }
      });
      setVisualMode('upload');
      toast.success('Image uploadée avec succès');
    };
    reader.onerror = () => {
      toast.error('Erreur lors de l\'upload de l\'image');
    };
    reader.readAsDataURL(file);
  };

  const handleUrlChange = (url) => {
    if (url) {
      setFormData({
        ...formData,
        content: {
          ...formData.content,
          image: url,
          uploaded_image: ''
        }
      });
      setVisualMode('url');
    } else {
      updateContentField('image', url);
    }
  };

  // Block types derived from CMS_BLOCK_REGISTRY (single source of truth)
  const iconMap = { Image, Type, Package, Gift, Navigation, LayoutGrid: Package };
  const blockTypes = getAdminBlockTypes().map(bt => ({
    value: bt.value,
    label: bt.label,
    icon: iconMap[bt.iconName] || Package,
  }));

  const renderContentFields = () => {
    const type = formData.type;

    switch (type) {
      case 'banner_fixed_centered':
      case 'banner':
        return (
          <>
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                value={formData.content.title || ''}
                onChange={(e) => updateContentField('title', e.target.value)}
                placeholder="Titre affiché dans la bannière"
                required
              />
              <p className="text-xs text-muted-foreground">
                Ce titre sera affiché en grand sur la bannière côté magasin
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Texte *</Label>
              <Textarea
                value={formData.content.text || ''}
                onChange={(e) => updateContentField('text', e.target.value)}
                placeholder="Texte descriptif de la bannière"
                rows={3}
                required
              />
              <p className="text-xs text-muted-foreground">
                Texte affiché sous le titre côté magasin
              </p>
            </div>
            
            {/* Couleur de fond */}
            <div className="space-y-2">
              <Label>Couleur de fond</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="color"
                  value={formData.content.background_color || '#000000'}
                  onChange={(e) => updateContentField('background_color', e.target.value)}
                  className="w-20 h-10 cursor-pointer"
                />
                <Input
                  type="text"
                  value={formData.content.background_color || ''}
                  onChange={(e) => updateContentField('background_color', e.target.value)}
                  placeholder="#000000"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => updateContentField('background_color', '')}
                >
                  Effacer
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Couleur de fond visible si pas d'image ou en overlay
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Image de fond (optionnel)</Label>
              
              <div className="flex gap-2 mb-3">
                <Button
                  type="button"
                  variant={visualMode === 'url' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setVisualMode('url');
                    updateContentField('uploaded_image', '');
                  }}
                >
                  URL d'image
                </Button>
                <Button
                  type="button"
                  variant={visualMode === 'upload' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setVisualMode('upload');
                    updateContentField('image', '');
                  }}
                >
                  Upload image
                </Button>
              </div>
              
              {visualMode === 'url' ? (
                <div>
                  <Input
                    value={formData.content.image || ''}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    placeholder="https://exemple.com/image.jpg"
                    disabled={visualMode !== 'url'}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Collez l'URL de votre image
                  </p>
                </div>
              ) : (
                <div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="cursor-pointer"
                    disabled={visualMode !== 'upload'}
                  />
                  {formData.content.uploaded_image && (
                    <div className="mt-2">
                      <img 
                        src={formData.content.uploaded_image} 
                        alt="Preview" 
                        className="max-h-32 rounded border"
                      />
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Sélectionnez une image depuis votre ordinateur
                  </p>
                </div>
              )}
            </div>
            
            {/* Couleurs du texte */}
            <div className="border-t pt-4 mt-4 space-y-4">
              <Label className="text-base font-semibold">Couleurs du texte</Label>
              
              {/* Couleur du titre */}
              <div className="space-y-2">
                <Label>Couleur du titre</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="color"
                    value={formData.content.title_color || '#ffffff'}
                    onChange={(e) => updateContentField('title_color', e.target.value)}
                    className="w-20 h-10 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={formData.content.title_color || ''}
                    onChange={(e) => updateContentField('title_color', e.target.value)}
                    placeholder="#FFFFFF"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => updateContentField('title_color', '')}
                  >
                    Défaut
                  </Button>
                </div>
              </div>
              
              {/* Couleur du texte */}
              <div className="space-y-2">
                <Label>Couleur du texte</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="color"
                    value={formData.content.text_color || '#ffffff'}
                    onChange={(e) => updateContentField('text_color', e.target.value)}
                    className="w-20 h-10 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={formData.content.text_color || ''}
                    onChange={(e) => updateContentField('text_color', e.target.value)}
                    placeholder="#FFFFFF"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => updateContentField('text_color', '')}
                  >
                    Défaut
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Section Bouton */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold mb-3 block">Bouton d'action</Label>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Texte du Bouton (optionnel)</Label>
                  <Input
                    value={formData.content.button_text || ''}
                    onChange={(e) => updateContentField('button_text', e.target.value)}
                    placeholder="Ex: Découvrir le catalogue"
                  />
                  <p className="text-xs text-muted-foreground">
                    Si vide, aucun bouton ne sera affiché
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Lien du Bouton (optionnel)</Label>
                  <Input
                    value={formData.content.button_link || ''}
                    onChange={(e) => updateContentField('button_link', e.target.value)}
                    placeholder="/dashboard/nouveautes"
                    disabled={!formData.content.button_text}
                  />
                  <p className="text-xs text-muted-foreground">
                    {!formData.content.button_text 
                      ? "Renseignez d'abord le texte du bouton"
                      : "URL de redirection interne ou externe"
                    }
                  </p>
                </div>
                
                {/* Couleur du bouton */}
                {formData.content.button_text && (
                  <>
                    <div className="space-y-2">
                      <Label>Couleur du bouton</Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          type="color"
                          value={formData.content.button_bg_color || '#ffffff'}
                          onChange={(e) => updateContentField('button_bg_color', e.target.value)}
                          className="w-20 h-10 cursor-pointer"
                        />
                        <Input
                          type="text"
                          value={formData.content.button_bg_color || ''}
                          onChange={(e) => updateContentField('button_bg_color', e.target.value)}
                          placeholder="#FFFFFF"
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => updateContentField('button_bg_color', '')}
                        >
                          Défaut
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Couleur du texte du bouton</Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          type="color"
                          value={formData.content.button_text_color || '#000000'}
                          onChange={(e) => updateContentField('button_text_color', e.target.value)}
                          className="w-20 h-10 cursor-pointer"
                        />
                        <Input
                          type="text"
                          value={formData.content.button_text_color || ''}
                          onChange={(e) => updateContentField('button_text_color', e.target.value)}
                          placeholder="#000000"
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => updateContentField('button_text_color', '')}
                        >
                          Défaut
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Section Planification - NOUVEAU */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold mb-3 block">📅 Planification de visibilité</Label>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Date de début */}
                <div className="space-y-2">
                  <Label htmlFor="banner-start-date">Date de début</Label>
                  <Input
                    id="banner-start-date"
                    type="datetime-local"
                    value={formData.content.start_date || ''}
                    onChange={(e) => updateContentField('start_date', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {formData.content.start_date 
                      ? `Visible à partir du ${new Date(formData.content.start_date).toLocaleString('fr-FR')}`
                      : 'Laisser vide = visible immédiatement'
                    }
                  </p>
                </div>
                
                {/* Date de fin */}
                <div className="space-y-2">
                  <Label htmlFor="banner-end-date">Date de fin</Label>
                  <Input
                    id="banner-end-date"
                    type="datetime-local"
                    value={formData.content.end_date || ''}
                    onChange={(e) => updateContentField('end_date', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {formData.content.end_date 
                      ? `Visible jusqu'au ${new Date(formData.content.end_date).toLocaleString('fr-FR')}`
                      : 'Laisser vide = visible indéfiniment'
                    }
                  </p>
                </div>
              </div>
              
              {/* Validation */}
              {formData.content.start_date && formData.content.end_date && 
               new Date(formData.content.end_date) < new Date(formData.content.start_date) && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  ⚠️ La date de fin doit être postérieure à la date de début
                </div>
              )}
              
              {/* Info */}
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                <p className="font-medium mb-1">💡 Comment fonctionne la planification :</p>
                <ul className="list-disc list-inside text-xs space-y-1">
                  <li>Sans dates : la bannière est toujours visible (si activée)</li>
                  <li>Avec date de début : visible à partir de cette date</li>
                  <li>Avec date de fin : masquée après cette date</li>
                  <li>Le toggle "Actif" a priorité : si désactivé, la bannière est toujours masquée</li>
                </ul>
              </div>
            </div>
          </>
        );

      case 'carousel_banner':
        return (
          <CarouselBannerEditor
            content={formData.content}
            onChange={(carouselContent) => setFormData({ ...formData, content: carouselContent })}
          />
        );

      case 'promotion_cards_grid':
        return (
          <PromotionCardsGridEditor
            content={formData.content}
            onChange={(gridContent) => setFormData({ ...formData, content: gridContent })}
          />
        );

      case 'partner_cards_grid':
        return (
          <PartnerCardsGridEditor
            content={formData.content}
            onChange={(gridContent) => setFormData({ ...formData, content: gridContent })}
          />
        );

      case 'navigation_cards':
        return (
          <NavigationCardsEditor
            content={formData.content}
            onChange={(navContent) => setFormData({ ...formData, content: navContent })}
          />
        );

      case 'modular_grid':
        return (
          <ModularGridEditor
            content={formData.content}
            onChange={(gridContent) => setFormData({ ...formData, content: gridContent })}
          />
        );

      case 'text':
        return (
          <div className="space-y-4">
            {/* Titre optionnel */}
            <div className="space-y-2">
              <Label>Titre (optionnel)</Label>
              <Input
                value={formData.content.title || ''}
                onChange={(e) => updateContentField('title', e.target.value)}
                placeholder="Titre de la section..."
              />
            </div>

            {/* Couleur de fond */}
            <div className="space-y-2">
              <Label htmlFor="text-bg-color">Couleur de fond (optionnel)</Label>
              <div className="flex gap-2 items-center">
                <Input
                  id="text-bg-color"
                  type="color"
                  value={formData.content.background_color || '#ffffff'}
                  onChange={(e) => updateContentField('background_color', e.target.value)}
                  className="w-20 h-10 cursor-pointer"
                />
                <Input
                  type="text"
                  value={formData.content.background_color || ''}
                  onChange={(e) => updateContentField('background_color', e.target.value)}
                  placeholder="#FFFFFF ou transparent"
                  className="flex-1"
                />
                {formData.content.background_color && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => updateContentField('background_color', '')}
                  >
                    Effacer
                  </Button>
                )}
              </div>
            </div>

            {/* Éditeur de texte avec formatage */}
            <div className="space-y-2">
              <Label>Contenu</Label>
              <RichTextEditor
                value={formData.content.text || ''}
                onChange={(content) => updateContentField('text', content)}
              />
            </div>
          </div>
        );

      case 'product_list':
        return (
          <>
            <div className="space-y-2">
              <Label>Filtre produits</Label>
              <Select
                value={formData.content.filter || 'all'}
                onValueChange={(value) => updateContentField('filter', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les produits</SelectItem>
                  <SelectItem value="is_new">Nouveautés</SelectItem>
                  <SelectItem value="is_offer">Offres</SelectItem>
                  <SelectItem value="bestsellers">Best-sellers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Limite d'affichage</Label>
              <Input
                type="number"
                value={formData.content.limit || 8}
                onChange={(e) => updateContentField('limit', parseInt(e.target.value) || 8)}
                min={1}
                max={50}
              />
            </div>
          </>
        );

      case 'vitrin_block':
        return (
          <>
            <div className="space-y-2">
              <Label>Emplacement marketing *</Label>
              <Select
                value={formData.content.placement_key || ''}
                onValueChange={(v) => updateContentField('placement_key', v)}
              >
                <SelectTrigger data-testid="vitrin-block-placement">
                  <SelectValue placeholder="Choisir un emplacement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="home_featured">Accueil — En vedette</SelectItem>
                  <SelectItem value="home_preorders">Accueil — Précommandes</SelectItem>
                  <SelectItem value="home_new_arrivals">Accueil — Nouveautés</SelectItem>
                  <SelectItem value="new_arrivals_featured">Nouveautés — Coup de projecteur</SelectItem>
                  <SelectItem value="preorders_featured">Précommandes — À ne pas manquer</SelectItem>
                  <SelectItem value="selection_month">Sélection du mois</SelectItem>
                  <SelectItem value="bestsellers_curated">Best-sellers — Sélection éditoriale</SelectItem>
                  <SelectItem value="partner_spotlight">Mise en avant partenaire</SelectItem>
                  <SelectItem value="seasonal_campaign">Campagne saisonnière</SelectItem>
                  <SelectItem value="tcg_spotlight">TCG — En vedette</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Titre (optionnel, remplace le titre de la vitrine)</Label>
              <Input
                value={formData.content.title || ''}
                onChange={(e) => updateContentField('title', e.target.value)}
                placeholder="Titre personnalisé..."
                data-testid="vitrin-block-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Sous-titre</Label>
              <Input
                value={formData.content.subtitle || ''}
                onChange={(e) => updateContentField('subtitle', e.target.value)}
                placeholder="Sous-titre optionnel..."
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Afficher le titre de la vitrine</Label>
              <Switch
                checked={formData.content.show_title !== false}
                onCheckedChange={(v) => updateContentField('show_title', v)}
              />
            </div>
          </>
        );

      case 'offer':
        return (
          <>
            <div className="space-y-2">
              <Label>Titre de l'offre</Label>
              <Input
                value={formData.content.offer_title || ''}
                onChange={(e) => updateContentField('offer_title', e.target.value)}
                placeholder="-20% sur tous les jeux"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.content.description || ''}
                onChange={(e) => updateContentField('description', e.target.value)}
                placeholder="Description de l'offre..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Couleur d'accent</Label>
              <Input
                type="color"
                value={formData.content.color || '#4f46e5'}
                onChange={(e) => updateContentField('color', e.target.value)}
              />
            </div>
          </>
        );

      default:
        return (
          <div className="space-y-2">
            <Label>Contenu JSON</Label>
            <Textarea
              value={JSON.stringify(formData.content, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  setFormData({ ...formData, content: parsed });
                } catch (err) {
                  // Invalid JSON, don't update
                }
              }}
              rows={6}
              className="font-mono text-sm"
            />
          </div>
        );
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!page) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Page non trouvée</p>
          <Button onClick={() => navigate('/dashboard/admin/pages')} className="mt-4">
            Retour aux pages
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard/admin/pages">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-heading font-bold" data-testid="page-blocks-title">
                Blocs - {page.title}
              </h1>
              <p className="text-muted-foreground mt-1">
                <code className="text-xs bg-gray-100 px-2 py-1 rounded">{page.slug}</code>
              </p>
            </div>
          </div>
          <Button onClick={() => handleOpenModal()} data-testid="add-block-button">
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un bloc
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border p-6">
            <div className="text-sm text-muted-foreground mb-1">Total Blocs</div>
            <div className="text-3xl font-bold">{blocks.length}</div>
          </div>
          <div className="bg-white rounded-lg border p-6">
            <div className="text-sm text-muted-foreground mb-1">Blocs Actifs</div>
            <div className="text-3xl font-bold text-green-600">
              {blocks.filter(b => b.is_active).length}
            </div>
          </div>
          <div className="bg-white rounded-lg border p-6">
            <div className="text-sm text-muted-foreground mb-1">Blocs Masqués</div>
            <div className="text-3xl font-bold text-orange-600">
              {blocks.filter(b => !b.is_active).length}
            </div>
          </div>
        </div>

        {/* Blocks List */}
        {blocks.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">Aucun bloc pour cette page</p>
            <Button onClick={() => handleOpenModal()}>
              <Plus className="h-4 w-4 mr-2" />
              Créer le premier bloc
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border p-4">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="blocks">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="space-y-3"
                  >
                    {blocks.map((block, index) => (
                      <Draggable key={block.id} draggableId={block.id} index={index}>
                        {(provided, snapshot) => (
                          <Card
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`${
                              snapshot.isDragging ? 'shadow-lg ring-2 ring-primary' : ''
                            } ${!block.is_active ? 'opacity-60 bg-gray-50' : ''}`}
                            data-testid={`block-${block.id}`}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-center gap-3">
                                <div
                                  {...provided.dragHandleProps}
                                  className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
                                  data-testid="drag-handle"
                                >
                                  <GripVertical className="w-5 h-5 text-gray-400" />
                                </div>

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge 
                                      variant={block.type === 'global_block_reference' ? 'default' : 'outline'} 
                                      className={`text-xs ${block.type === 'global_block_reference' ? 'bg-blue-600' : ''}`}
                                    >
                                      {block.type === 'global_block_reference' 
                                        ? `🌐 ${block.content?.global_block_type || 'global'}` 
                                        : block.type}
                                    </Badge>
                                    <h3 className="font-semibold truncate">
                                      {block.label || block.title}
                                    </h3>
                                    {!block.is_active && (
                                      <Badge variant="outline" className="text-xs text-orange-600">
                                        <EyeOff className="w-3 h-3 mr-1" />
                                        Masqué
                                      </Badge>
                                    )}
                                    {/* Badge zone pour pages listing */}
                                    {isListingPage && (block.zone === 'header' || block.zone === 'footer') && (
                                      <Badge variant="outline" className={`text-xs ${block.zone === 'header' ? 'text-blue-600 border-blue-200' : 'text-purple-600 border-purple-200'}`}>
                                        {block.zone === 'header' ? 'TOP' : 'BOTTOM'}
                                      </Badge>
                                    )}
                                  </div>
                                  {block.label && (
                                    <p className="text-xs text-muted-foreground mb-1">
                                      📌 {block.label}
                                    </p>
                                  )}
                                  {block.type === 'global_block_reference' && block.content?.global_block_title && (
                                    <p className="text-xs text-blue-600 mb-1">
                                      🔗 Lié à: {block.content.global_block_title}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span>Ordre: {block.order}</span>
                                  </div>
                                </div>

                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleBlockStatus(block.id, block.is_active)}
                                    title={block.is_active ? 'Masquer' : 'Afficher'}
                                    data-testid={`toggle-block-${block.id}`}
                                  >
                                    {block.is_active ? (
                                      <Eye className="w-4 h-4" />
                                    ) : (
                                      <EyeOff className="w-4 h-4 text-green-600" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenModal(block)}
                                    data-testid={`edit-block-${block.id}`}
                                  >
                                    <Edit className="w-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteTarget(block)}
                                    data-testid={`delete-block-${block.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}

        {/* Modal */}
        <Dialog open={showModal} onOpenChange={handleCloseModal}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="block-modal">
            <DialogHeader>
              <DialogTitle>
                {editingBlock ? 'Modifier le bloc' : 'Créer un nouveau bloc'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* If editing an existing global_block_reference, show read-only info */}
              {editingBlock && blockCategory === 'global' ? (
                <div className="space-y-2">
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                    <p className="font-semibold text-blue-900 mb-1">Bloc Global</p>
                    <p className="text-blue-700">
                      {globalBlocks.find(gb => gb.id === selectedGlobalBlockId)?.title || 'Bloc global lié'}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      Gérez ce bloc dans <Link to="/dashboard/admin/global-blocks" className="text-primary underline">Blocs Globaux</Link>
                    </p>
                  </div>
                </div>
              ) : (
                /* Classic block type selector — always shown for new blocks */
                <>
                  <div className="space-y-2">
                    <Label htmlFor="type">Type de bloc *</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) => setFormData({ ...formData, type: value })}
                      disabled={!!editingBlock}
                    >
                      <SelectTrigger data-testid="block-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {blockTypes.map((type) => {
                          const Icon = type.icon;
                          return (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                {type.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="label">Libellé * (visible uniquement dans l'admin)</Label>
                <Input
                  id="label"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  required
                  data-testid="block-label-input"
                  placeholder="Ex: Bannière homepage principale"
                />
                <p className="text-xs text-muted-foreground">
                  Ce libellé sert à identifier le bloc dans l'interface admin uniquement
                </p>
              </div>

              {/* Zone d'affichage: visible uniquement pour pages listing hybrides */}
              {isListingPage && (
                <div data-testid="zone-selector-listing">
                  <Label className="mb-2 block font-semibold">Position du bloc</Label>
                  <Select
                    value={formData.zone === 'footer' ? 'bottom' : 'top'}
                    onValueChange={(v) => setFormData({ ...formData, zone: v === 'bottom' ? 'footer' : 'header' })}
                  >
                    <SelectTrigger data-testid="select-block-zone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top">TOP — Avant le contenu listing</SelectItem>
                      <SelectItem value="bottom">BOTTOM — Après le contenu listing</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Détermine où le bloc apparaît sur la page listing
                  </p>
                </div>
              )}

              {/* Afficher le contenu uniquement pour les blocs classiques */}
              {blockCategory === 'classic' && (
                <div className="border-t pt-4">
                  <Label className="mb-3 block font-semibold">Contenu du bloc</Label>
                  {renderContentFields()}
                </div>
              )}

              {/* Padding Externe - UNIVERSEL pour tous les blocs */}
              <div className="border-t pt-4">
                <ExternalPaddingControl
                  value={formData.content.external_padding || {}}
                  onChange={(newPadding) => updateContentField('external_padding', newPadding)}
                />
              </div>

              {/* Surface Variants — Profondeur visuelle CMS */}
              <div className="border-t pt-4 space-y-4">
                <Label className="text-sm font-semibold text-muted-foreground">Surface (profondeur visuelle)</Label>
                
                <div className="grid grid-cols-3 gap-4">
                  {/* Background Variant */}
                  <div className="space-y-1">
                    <Label className="text-xs">Fond</Label>
                    <Select
                      value={formData.content.surface_bg || 'flat'}
                      onValueChange={(v) => updateContentField('surface_bg', v)}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid="surface-bg-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flat">Aucun</SelectItem>
                        <SelectItem value="soft-gradient">Dégradé doux</SelectItem>
                        <SelectItem value="radial-soft">Halo central</SelectItem>
                        <SelectItem value="subtle-pattern">Texture points</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Border Variant */}
                  <div className="space-y-1">
                    <Label className="text-xs">Contour</Label>
                    <Select
                      value={formData.content.surface_border || 'none'}
                      onValueChange={(v) => updateContentField('surface_border', v)}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid="surface-border-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        <SelectItem value="subtle">Subtil</SelectItem>
                        <SelectItem value="accent">Accent chaud</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Shadow Variant */}
                  <div className="space-y-1">
                    <Label className="text-xs">Ombre</Label>
                    <Select
                      value={formData.content.surface_shadow || 'none'}
                      onValueChange={(v) => updateContentField('surface_shadow', v)}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid="surface-shadow-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucune</SelectItem>
                        <SelectItem value="soft">Douce</SelectItem>
                        <SelectItem value="elevated">Élevée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Mini aperçu */}
                {(formData.content.surface_bg !== 'flat' || formData.content.surface_border !== 'none' || formData.content.surface_shadow !== 'none') && (
                  <div 
                    className="h-12 rounded-lg flex items-center justify-center text-xs text-muted-foreground"
                    style={{
                      ...(formData.content.surface_bg === 'soft-gradient' ? { background: 'linear-gradient(180deg, rgba(247,245,242,0.4) 0%, rgba(255,255,255,0) 100%)' } : {}),
                      ...(formData.content.surface_bg === 'radial-soft' ? { background: 'radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.6) 0%, rgba(247,245,242,0) 70%)' } : {}),
                      ...(formData.content.surface_border === 'subtle' ? { border: '1px solid rgba(0,0,0,0.06)' } : {}),
                      ...(formData.content.surface_border === 'accent' ? { border: '1px solid rgba(180,160,130,0.15)' } : {}),
                      ...(formData.content.surface_shadow === 'soft' ? { boxShadow: '0 1px 2px rgba(140,120,90,0.04), 0 4px 12px rgba(140,120,90,0.06)' } : {}),
                      ...(formData.content.surface_shadow === 'elevated' ? { boxShadow: '0 2px 4px rgba(140,120,90,0.04), 0 8px 24px rgba(140,120,90,0.08)' } : {}),
                    }}
                  >
                    Aperçu surface
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  data-testid="block-active-switch"
                />
                <Label htmlFor="is_active" className="cursor-pointer">Bloc actif</Label>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  data-testid="cancel-button"
                >
                  Annuler
                </Button>
                <Button type="submit" data-testid="save-block-button">
                  {editingBlock ? 'Mettre à jour' : 'Créer le bloc'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <DestructiveActionDialog
          open={!!deleteTarget}
          onOpenChange={() => setDeleteTarget(null)}
          title="Supprimer le bloc"
          itemName={deleteTarget?.title || deleteTarget?.type || ''}
          description="Le bloc de contenu sera définitivement supprimé de cette page."
          onConfirm={handleDelete}
        />
      </div>
    </DashboardLayout>
  );
};

export default AdminPageBlocks;
